import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import toast from 'react-hot-toast';

function getLoginErrorMessage(err) {
  if (err?.response?.data?.error) return err.response.data.error;
  if (err?.message === 'Network Error') return 'Cannot reach the server. Make sure the backend is running on port 4000.';
  return err?.message || 'Login failed';
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    const normalizedUsername = username.trim();
    if (!normalizedUsername || !password) {
      toast.error('Enter your username and password');
      return;
    }

    setLoading(true);
    try {
      await login(normalizedUsername, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Login request failed:', err?.response?.data || err);
      toast.error(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflowX: 'hidden', padding: '20px' }}>
      {/* Dynamic Mobile Styles */}
      <style>{`
        .login-card {
          display: flex;
          width: 100%;
          max-width: 900px;
          background: var(--surface);
          border-radius: var(--r-xl);
          box-shadow: var(--shadow-lg);
          overflow: hidden;
          flex-direction: row;
        }
        .sidebar {
          width: 360px;
          padding: 48px 40px;
          background: var(--brand);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }
        .form-section {
          flex: 1;
          padding: 48px 44px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        @media (max-width: 850px) {
          .login-card { flex-direction: column; max-width: 450px; }
          .sidebar { width: 100%; padding: 32px; min-height: 200px; }
          .sidebar-features { display: none; } /* Hide features on mobile to save space */
          .form-section { padding: 32px 24px; }
        }
      `}</style>

      {/* Background Decor */}
      <div style={{ position: 'absolute', top: -160, right: -160, width: 520, height: 520, borderRadius: '50%', background: 'radial-gradient(circle at 60% 40%, #dce9ff 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'absolute', bottom: -120, left: -120, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle at 40% 60%, #dff4eb 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      <div className="scale-in login-card" style={{ zIndex: 1 }}>
        {/* Left Sidebar */}
        <div className="sidebar">
          <div style={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', background: 'rgba(255,255,255,.07)' }} />
          
          <div className="fade-up" style={{ display: 'flex', alignItems: 'center', gap: 12, zIndex: 1 }}>
            <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,.2)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6zm0 8a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2zm12-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-.3px' }}>Daima POS</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', letterSpacing: '.8px', textTransform: 'uppercase' }}>Point of Sale</div>
            </div>
          </div>

          <div className="fade-up sidebar-features" style={{ zIndex: 1, animationDelay: '.1s', marginTop: 40 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#fff', lineHeight: 1.25, letterSpacing: '-.5px', marginBottom: 14 }}>Your business,<br />always running.</div>
            {['Real-time inventory sync', 'Multi-device POS terminals', 'M-Pesa & Cash support'].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 13, color: 'rgba(255,255,255,.8)' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, flexShrink: 0 }}>✓</div>
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Right Form Section */}
        <div className="form-section">
          <div className="fade-up" style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.4px', marginBottom: 6 }}>Sign in</div>
          <div className="fade-up" style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 28, animationDelay: '.05s' }}>Access your sales dashboard.</div>

          <form onSubmit={handleSubmit}>
            <div className="form-group fade-up" style={{ animationDelay: '.15s', marginBottom: 16 }}>
              <label className="form-label" style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Username</label>
              <input 
                className="form-input" 
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)' }}
                value={username} 
                onChange={e => setUsername(e.target.value)} 
                placeholder="Enter username" 
                autoComplete="username" 
              />
            </div>
            <div className="form-group fade-up" style={{ animationDelay: '.2s', marginBottom: 20 }}>
              <label className="form-label" style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Password</label>
              <input 
                className="form-input" 
                type="password"
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg2)' }}
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                placeholder="Enter password" 
                autoComplete="current-password" 
              />
            </div>

            <button className="btn btn-primary fade-up" type="submit" disabled={loading}
              style={{ 
                width: '100%', 
                justifyContent: 'center', 
                padding: '14px', 
                fontSize: 15, 
                fontWeight: 600, 
                animationDelay: '.3s',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}>
              {loading ? <div className="spinner spinner-sm" style={{ borderTopColor: '#fff' }} /> : (
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              )}
              {loading ? 'Signing in...' : 'Sign in to Daima POS'}
            </button>
            
            <div className="fade-up" style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: 'var(--ink3)', animationDelay: '.35s' }}>
              Need help? Contact your administrator.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}